import {
	AfterViewInit,
	Component,
	computed,
	ElementRef,
	inject,
	input,
	InputSignal,
	OnChanges,
	OnInit,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	SimpleChanges,
	Type,
	WritableSignal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CalendarWeekViewEventComponent } from './components/calendar-week-view-event/calendar-week-view-event.component';
import { ICalendarWeekViewAdjustedEvent, ICalendarWeekViewEvent } from './interfaces/icalendar-week-view-event.interface';
import { setCssValue } from '../../functions/set-css-value.function';
import { CalendarDayHeaderComponent } from './components/calendar-day-header/calendar-day-header.component';
import { ICalendarWeekViewConfig } from './interfaces/icalendar-week-view-config.interface';
import { MDDatePickerComponent, MDIconComponent } from '@melodic-ui/components';
import { isSameWeek, startOfWeek, endOfWeek } from 'date-fns';
import { isStartOfDay } from '@lb/functions/is-start-of-day.function';
import { getTimeOptions } from '@lb/functions/get-time-options.function';
import { TimezoneContextService } from '@lb/config/timezone.provider';

export const calendarConfigDefaults: ICalendarWeekViewConfig = {
	timeIncrement: 30,
	skipOdd: true,
	showWeekNavigation: false,
	initDate: new Date(),
	startHour: 0,
	endHour: 24,
	dateHeaderComponent: CalendarDayHeaderComponent as Type<Component>
};

@Component({
	selector: 'lb-calendar-week-view',
	standalone: true,
	imports: [CommonModule, MDIconComponent, MDDatePickerComponent],
	templateUrl: './calendar-week-view.component.html',
	styleUrl: './calendar-week-view.component.scss'
})
export class CalendarWeekViewComponent implements OnInit, AfterViewInit, OnChanges {
	private _elementRef: ElementRef = inject(ElementRef);
	private _timezoneContextService = inject(TimezoneContextService);

	private _timeRows: WritableSignal<HTMLElement[]> = signal([]);

	private _getCalendarRowIDByTime = (date: Date, type: 'start' | 'end') => {
		return computed(() => {
			const hours: number = date.getHours();
			const minutes: number = date.getMinutes();
			const totalMinutes: number = hours * 60 + minutes;

			// check if hour is less than the first row
			if (hours < (this.calendarConfig().startHour || 0)) {
				return 1;
			}

			// check hour and minute is greater than last row
			const lastTimeOfDay = this.timesOfDay()[this.timesOfDay().length - 1];
			if (hours > (this.calendarConfig().endHour || 24) || (hours === (this.calendarConfig().endHour || 24) && minutes > lastTimeOfDay.minute)) {
				return this.timesOfDay().length + 1;
			}

			if (type === 'start') {
				const sortedRows = this._timeRows()
					.map((row: HTMLElement) => {
						const hour = parseInt(row.getAttribute('data-hour-of-day') || '0');
						const minute = parseInt(row.getAttribute('data-minute-of-hour') || '0');

						return {
							id: parseInt(row.getAttribute('data-grid-row') || '0'),
							totalMinutes: hour * 60 + minute
						};
					})
					.sort((a, b) => a.totalMinutes - b.totalMinutes);

				return (
					sortedRows.find((row: { id: number; totalMinutes: number }) => {
						return totalMinutes <= row.totalMinutes;
					})?.id || 1
				);
			} else {
				const sortedRows = this._timeRows()
					.map((row: HTMLElement) => {
						const hour = parseInt(row.getAttribute('data-hour-of-day') || '0');
						const minute = parseInt(row.getAttribute('data-minute-of-hour') || '0');

						return {
							id: parseInt(row.getAttribute('data-grid-row') || '0'),
							totalMinutes: hour * 60 + minute
						};
					})
					.sort((a, b) => b.totalMinutes - a.totalMinutes);

				const row = sortedRows.find((row: { id: number; totalMinutes: number }) => {
					return totalMinutes > row.totalMinutes;
				});

				return (row?.id || this.timesOfDay().length) + 1;
			}
		});
	};

	public selectedDate: InputSignal<Date> = input<Date>(new Date());
	public configuration: InputSignal<ICalendarWeekViewConfig> = input<ICalendarWeekViewConfig>(calendarConfigDefaults);
	public events: InputSignal<ICalendarWeekViewEvent[]> = input<ICalendarWeekViewEvent[]>([]);

	public navigation: OutputEmitterRef<{ startDate: Date; endDate: Date }> = output<{ startDate: Date; endDate: Date }>();

	public calendarConfig: WritableSignal<ICalendarWeekViewConfig> = signal<ICalendarWeekViewConfig>(calendarConfigDefaults);

	public initDate: WritableSignal<Date> = signal<Date>(new Date());
	public daysOfWeek: Signal<{ date: Date; format: string }[]> = computed(() => {
		return this.getWeekDates(this.initDate());
	});
	public timesOfDay: Signal<{ id: string; hour: number; minute: number; label: string }[]> = computed(() => {
		return this.populateTimes(this.calendarConfig().timeIncrement || 30);
	});

	public monthName: Signal<string> = computed(() => {
		const timezone = this._timezoneContextService.getEffectiveTimezone();

		if (timezone?.timeZoneName) {
			return this.initDate().toLocaleDateString('en-US', {
				month: 'long',
				timeZone: timezone.timeZoneName
			});
		}

		return this.initDate().toLocaleDateString('en-US', { month: 'long' });
	});

	public yearName: Signal<string> = computed(() => {
		const timezone = this._timezoneContextService.getEffectiveTimezone();

		if (timezone?.timeZoneName) {
			return this.initDate().toLocaleDateString('en-US', {
				year: 'numeric',
				timeZone: timezone.timeZoneName
			});
		}

		return this.initDate().toLocaleDateString('en-US', { year: 'numeric' });
	});

	public eventsAdjusted: Signal<ICalendarWeekViewAdjustedEvent[]> = computed(() => {
		const events: ICalendarWeekViewEvent[] = this.events();
		const eventsAdjusted: ICalendarWeekViewAdjustedEvent[] = [];

		events.forEach((event: ICalendarWeekViewEvent) => {
			if (!isSameWeek(event.startDate, this.initDate())) {
				return;
			}

			const startDate = event.startDate;
			const endDate = event.endDate;

			const gridColumnStart = startDate.getDay() + 2; // index of day + 1 for time column
			const gridColumnEnd = endDate.getDay() + (isStartOfDay(endDate) ? 2 : 3); // index of day + 1 for time column + 1 for next column

			const gridRowStart = this._getCalendarRowIDByTime(startDate, 'start');
			const gridRowEnd = this._getCalendarRowIDByTime(endDate, 'end');

			eventsAdjusted.push({
				...event,
				component: event.component || (CalendarWeekViewEventComponent as Type<Component>),
				gridColumnStart: gridColumnStart,
				gridColumnEnd: gridColumnEnd,
				gridRowStart: gridRowStart(),
				gridRowEnd: gridRowEnd()
			});
		});

		return eventsAdjusted;
	}, {});

	public datePickerActive: WritableSignal<boolean> = signal<boolean>(false);

	ngOnInit(): void {
		this.calendarConfig.set({ ...calendarConfigDefaults, ...this.configuration() });

		const initDate = this.calendarConfig().initDate || new Date();
		initDate.setHours(0, 0, 0, 0);
		this.initDate.set(initDate);

		const startDate = startOfWeek(initDate);
		const endDate = endOfWeek(initDate);

		this.navigation.emit({ startDate, endDate });
	}

	ngAfterViewInit(): void {
		this._timeRows.set(Array.from(this._elementRef.nativeElement.querySelectorAll('.calendar-row')));

		const date = new Date();
		date.setHours(12, 0, 0, 0);
		const noonID = this._getCalendarRowIDByTime(date, 'start');
		document.querySelector(`[data-grid-row="${noonID()}"]`)?.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' });
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['selectedDate'] && !changes['selectedDate'].firstChange && !!this.selectedDate) {
			this.setDate([this.selectedDate()]);
		}
	}

	public toggleDatePicker(): void {
		this.datePickerActive.set(!this.datePickerActive());
	}

	public setDate(date: Date[]): void {
		const initDate = date[0];
		initDate.setHours(0, 0, 0, 0);
		this.initDate.set(initDate);

		this.datePickerActive.set(false);

		const startDate = startOfWeek(initDate);
		const endDate = endOfWeek(initDate);

		this.navigation.emit({ startDate, endDate });
	}

	public setToday(): void {
		const initDate = new Date();
		initDate.setHours(0, 0, 0, 0);
		this.initDate.set(initDate);

		const startDate = startOfWeek(initDate);
		const endDate = endOfWeek(initDate);

		this.navigation.emit({ startDate, endDate });
	}

	public nextWeek(): void {
		const initDate = new Date(this.initDate());
		initDate.setDate(this.initDate().getDate() + 7);
		initDate.setHours(0, 0, 0, 0);
		this.initDate.set(initDate);

		const startDate = startOfWeek(initDate);
		const endDate = endOfWeek(initDate);

		this.navigation.emit({ startDate, endDate });
	}

	public previousWeek(): void {
		const initDate = new Date(this.initDate());
		initDate.setDate(this.initDate().getDate() - 7);
		initDate.setHours(0, 0, 0, 0);
		this.initDate.set(initDate);

		const startDate = startOfWeek(initDate);
		const endDate = endOfWeek(initDate);

		this.navigation.emit({ startDate, endDate });
	}

	private populateTimes(timeIncrement: number = 30): { id: string; hour: number; minute: number; label: string }[] {
		const times = getTimeOptions(
			this.calendarConfig().startHour || 0,
			this.calendarConfig().endHour || 24,
			timeIncrement,
			false,
			false,
			(rowCount: number) => {
				setCssValue(this._elementRef.nativeElement, '--calendar-week-view-rows', `${rowCount}`);
			}
		);

		return times;
	}

	private getWeekDates(date: Date): { date: Date; format: string }[] {
		const startDate = new Date(date);
		startDate.setDate(date.getDate() - date.getDay());

		const weekDates: { date: Date; format: string }[] = [];
		for (let i = 0; i < 7; i++) {
			const currentDate = new Date(startDate);
			currentDate.setDate(startDate.getDate() + i);
			weekDates.push({
				date: currentDate,
				format: currentDate.toLocaleDateString('en-US', {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit'
				})
			});
		}

		return weekDates;
	}
}
